import { IFraudTrendEvidence } from 'src/ts/interfaces/listCheck';
import { IXraySpanPage } from 'src/ts/interfaces/xray';

export interface DescriptorKey {
  name: string;
  start: number;
  end: number;
}

export interface FontDescriptor {
  offset: number;
  text: string;
  keys: DescriptorKey[];
}

export interface HeaderLine {
  page: number;
  text: string;
}

export interface HeaderBarRule {
  bank: string;
  firstPage: number;
  mark: string;
  expected: string;
  retyped: string;
}

export interface KeyOrderRow {
  fonts: number;
  keys: string[];
  moved: number[];
}

export interface AddedFontsCard {
  fired: boolean;
  total: number;
  expected: string[];
  others: KeyOrderRow[];
}

export interface HeaderBarsCard {
  rule: HeaderBarRule;
  fired: boolean;
  source: 'separator' | 'bars';
  retyped: number;
  total: number;
}

type EvidenceFeatures = Record<string, unknown>;

interface EvidenceFeature {
  status?: string;
  features?: EvidenceFeatures;
}

interface EvidenceVerification {
  verification?: string;
  extracted_features?: Record<string, EvidenceFeature>;
}

interface ParsedDict {
  end: number;
  keys: DescriptorKey[];
  type: string | null;
}

const MAX_DICT_LENGTH = 5000;
const DELIMITER = /[\s()<>[\]{}/%]/;

// Per PDF provider: the page the repeated header starts on, the text that
// marks it, and what its bars copy as in an original file and when retyped.
const HEADER_BAR_RULES: Record<string, HeaderBarRule> = {
  bank_of_america: {
    bank: 'Bank of America',
    firstPage: 2,
    mark: 'Account #',
    expected: '!',
    retyped: '|'
  }
};

const skipSpace = (s: string, i: number): number => {
  let at = i;
  while (at < s.length && /\s/.test(s[at])) at += 1;
  return at;
};

const tokenEnd = (s: string, i: number, limit: number): number => {
  let at = i;
  while (at < limit && !DELIMITER.test(s[at])) at += 1;
  return at;
};

const literalEnd = (s: string, i: number, limit: number): number => {
  let depth = 0;
  for (let at = i; at < limit; at += 1) {
    if (s[at] === '\\') at += 1;
    else if (s[at] === '(') depth += 1;
    else if (s[at] === ')') {
      depth -= 1;
      if (depth === 0) return at + 1;
    }
  }
  return -1;
};

const valueEnd = (s: string, i: number, limit: number): number => {
  if (s.startsWith('<<', i)) return parseDict(s, i, limit)?.end ?? -1;
  if (s[i] === '<') {
    const close = s.slice(i, limit).indexOf('>');
    return close === -1 ? -1 : i + close + 1;
  }
  if (s[i] === '(') return literalEnd(s, i, limit);
  if (s[i] === '[') {
    let at = skipSpace(s, i + 1);
    while (at < limit && s[at] !== ']') {
      const end = valueEnd(s, at, limit);
      if (end === -1 || end === at) return -1;
      at = skipSpace(s, end);
    }
    return at < limit ? at + 1 : -1;
  }
  if (s[i] === '/') return tokenEnd(s, i + 1, limit);
  return tokenEnd(s, i, limit);
};

function parseDict(s: string, start: number, limit: number): ParsedDict | null {
  const keys: DescriptorKey[] = [];
  let type: string | null = null;
  let at = skipSpace(s, start + 2);
  let afterKey = false;
  while (at < limit) {
    if (s.startsWith('>>', at)) return { end: at + 2, keys, type };
    if (s[at] === '/' && !afterKey) {
      const end = tokenEnd(s, at + 1, limit);
      keys.push({ name: s.slice(at + 1, end), start: at - start, end: end - start });
      afterKey = true;
      at = skipSpace(s, end);
    } else {
      const end = valueEnd(s, at, limit);
      if (end === -1 || end <= at) return null;
      if (afterKey && keys[keys.length - 1].name === 'Type' && s[at] === '/') {
        type = s.slice(at + 1, end);
      }
      afterKey = false;
      at = skipSpace(s, end);
    }
  }
  return null;
}

export const fontDescriptors = (byteCode: string | null | undefined): FontDescriptor[] => {
  const found: FontDescriptor[] = [];
  if (!byteCode) return found;
  let at = byteCode.indexOf('<<');
  while (at !== -1) {
    const limit = Math.min(byteCode.length, at + MAX_DICT_LENGTH);
    const dict = parseDict(byteCode, at, limit);
    if (dict?.type === 'FontDescriptor') {
      found.push({ offset: at, text: byteCode.slice(at, dict.end), keys: dict.keys });
      at = byteCode.indexOf('<<', dict.end);
    } else {
      at = byteCode.indexOf('<<', at + 2);
    }
  }
  return found;
};

const order = (descriptor: FontDescriptor): string => descriptor.keys.map((k) => k.name).join(' ');

export const expectedKeyOrder = (descriptors: FontDescriptor[]): string[] | null => {
  const counts = new Map<string, number>();
  descriptors.forEach((d) => counts.set(order(d), (counts.get(order(d)) ?? 0) + 1));
  const best = Array.from(counts).reduce<[string, number] | null>(
    (top, entry) => (!top || entry[1] > top[1] ? entry : top),
    null
  );
  return best ? best[0].split(' ') : null;
};

// The /FontName value without its six-letter subset tag, so AAAAAB+Arial reads Arial.
export const fontName = (descriptor: FontDescriptor): string | null => {
  const key = descriptor.keys.find((k) => k.name === 'FontName');
  if (!key) return null;

  const start = skipSpace(descriptor.text, key.end);
  if (descriptor.text[start] !== '/') return null;

  const name = descriptor.text.slice(
    start + 1,
    tokenEnd(descriptor.text, start + 1, descriptor.text.length)
  );
  return name.replace(/^[A-Z]{6}\+/, '') || null;
};

export const movedKeys = (keys: string[], expected: string[]): number[] =>
  keys.flatMap((key, index) => (expected.indexOf(key) === index ? [] : [index]));

export const headerBarRule = (provider: string | null | undefined): HeaderBarRule | null =>
  (provider && HEADER_BAR_RULES[provider]) || null;

export const headerLines = (
  spans: IXraySpanPage[] | null | undefined,
  rule: HeaderBarRule
): HeaderLine[] => {
  const lines = new Map<number, string>();
  (spans ?? []).forEach(({ page, items }) => {
    if (page < rule.firstPage || lines.has(page)) return;
    const header = (items ?? []).find((item) => item.text.includes(rule.mark));
    if (header) lines.set(page, header.text);
  });
  return Array.from(lines, ([page, text]) => ({ page, text })).sort((a, b) => a.page - b.page);
};

export const expectedHeader = (text: string, rule: HeaderBarRule): string =>
  text.split(rule.retyped).join(rule.expected);

const CHECKED = ['fired', 'ok'];

const featuresOf = (
  evidence: IFraudTrendEvidence | null | undefined,
  verification: string
): Record<string, EvidenceFeature> => {
  const verifications = (evidence?.verifications ?? []) as EvidenceVerification[];
  return verifications.find((v) => v.verification === verification)?.extracted_features ?? {};
};

const checked = (features: Record<string, EvidenceFeature>, rules: string[]): boolean =>
  rules.every((rule) => CHECKED.includes(features[rule]?.status ?? ''));

const count = (feature: EvidenceFeature | undefined, key: string): number => {
  const value = feature?.features?.[key];
  return typeof value === 'number' ? value : 0;
};

// Null when either rule abstained, errored or is missing, or no key orders came back, so the card says it couldn't check.
export const addedFontsCard = (
  evidence: IFraudTrendEvidence | null | undefined
): AddedFontsCard | null => {
  const features = featuresOf(evidence, 'bank_of_america_reembedded');
  const offOrder = features.boa_fonts_off_default_order;
  const packing = features.boa_mixed_packing;
  if (!checked(features, ['boa_fonts_off_default_order', 'boa_mixed_packing'])) return null;

  const byOrder = (features.boa_key_orders?.features?.fonts_by_order ?? {}) as Record<
    string,
    number
  >;
  const orders = Object.entries(byOrder).map(([order, fonts]) => ({
    fonts,
    keys: order.split(' ')
  }));
  if (!orders.length) return null;
  const expected = orders.reduce<(typeof orders)[number] | null>(
    (top, order) => (!top || order.fonts > top.fonts ? order : top),
    null
  );
  const expectedKeys = expected?.keys ?? [];

  return {
    fired: offOrder.status === 'fired' || packing.status === 'fired',
    total: count(offOrder, 'fonts_checked'),
    expected: expectedKeys,
    others: orders
      .filter((order) => order !== expected)
      .map((order) => ({ ...order, moved: movedKeys(order.keys, expectedKeys) }))
  };
};

// The separator line unless only boa_bars fired, as the ticket's Header bars rules say.
export const headerBarsCard = (
  evidence: IFraudTrendEvidence | null | undefined
): HeaderBarsCard | null => {
  const rule = headerBarRule(evidence?.template as string | undefined);
  const features = featuresOf(evidence, 'bank_of_america_exclamation');
  const separator = features.boa_separator;
  const bars = features.boa_bars;
  if (!rule || !checked(features, ['boa_separator', 'boa_bars'])) return null;

  if (bars.status === 'fired' && separator.status !== 'fired') {
    return {
      rule,
      fired: true,
      source: 'bars',
      retyped: count(bars, 'copy_pipe'),
      total: count(bars, 'bars_drawn')
    };
  }

  const retyped = count(separator, 'separators_pipe');
  return {
    rule,
    fired: separator.status === 'fired',
    source: 'separator',
    retyped,
    total: count(separator, 'separators_bang') + retyped
  };
};
